/**
 * FOLLOWING A CALL THAT BECAME A MEETING.
 *
 * "Move to meeting" used to strand everybody except the person who pressed it.
 * They navigated on their own HTTP response; everyone else got a call that
 * simply stopped, with no invite, no navigation and nothing on screen at all
 * until the 30-second reconciliation poll produced a generic "Call ended".
 * `call.escalated` was a declared durable event with ZERO handlers in this
 * entire app.
 *
 * THE RULE IS THE ONE `useTransferredCall` ALREADY FOLLOWS for a merge: the
 * event names who moved, and a client that finds itself in that list goes. The
 * only extra step is entitlement.
 *
 * WHY A JOIN CODE AND NOT A BARE NAVIGATION. Arriving at `/meet/<id>` with no
 * participant row shows a pre-join screen for a meeting this user has never
 * joined; redeeming first is what writes the row, admits them past the lobby and
 * puts them in the roster, so the navigation lands somewhere real rather than
 * somewhere merely readable.
 *
 * THE CODE IS NOT THE ENTITLEMENT ANY MORE, and that matters here. The escalate
 * route seeds a `meeting_invitees` row for every moved party and sets the
 * meeting's lobby to 'invited', so `codeGrantsEntry` (apps/api
 * services/meetingAccess.ts) answers true for them on its FIRST line — through
 * `canRead` — and false for anybody else, code or no code. The code is a pointer
 * to the meeting, not the key to it. It is still delivered narrowly (the server
 * fans this event out twice; only the movers' copy carries it), and this client
 * still refuses to act on a copy without one — belt, braces, and a third thing.
 *
 * WHAT IT DELIBERATELY DOES NOT DO IS JOIN THE MEDIA. Redeeming the code gets
 * the person through the door; the meeting's own pre-join screen is what turns
 * their camera on. Auto-joining would be one line — the meeting route accepts
 * `state: { autoJoin: true }` — and it would open the CAMERA of somebody who
 * was on an audio call, because the auto-join path uses the default settings
 * and those have `camera: true`. A surprise camera is not an acceptable price
 * for saving a click.
 */
import type { CallParticipant, CallPartyState } from '@cyphex/types/calls';
import type { RealtimeEvent } from '@cyphex/types/realtime';
import { apiPost } from '@/lib/api/client';

export interface EscalationInvite {
  /** The call that ended by becoming a meeting. */
  readonly callId: string;
  readonly meetingId: string;
  /** Bearer credential; the entitlement this user does not otherwise have. */
  readonly joinCode: string;
}

/**
 * The escalation THIS user has to follow, or `null`.
 *
 * Four ways to be `null`, and each one is a real frame that arrives:
 *
 *   • not an escalation at all — every other `call.*` event reaches this same
 *     socket callback;
 *   • MY OWN escalation. The actor is already navigating on the response to
 *     their own POST, and following the event as well would race that
 *     navigation with a redundant `join-by-code` against a meeting they own;
 *   • somebody else's. The event is fanned to every user who has ever had a
 *     row on the call — a person who declined it an hour ago included — and
 *     only the roster read before the call was settled says who was actually
 *     in the room;
 *   • THE ANNOUNCEMENT COPY, which carries no `joinCode` at all. The server
 *     fans `call.escalated` out twice on purpose (apps/api services/calls.ts
 *     `planEscalationFanout`): everybody on the call gets the news, and only the
 *     people who were moved get the credential on their own `user:` topic. A
 *     moved party therefore sees BOTH copies, in either order, and this is what
 *     makes the code-free one a no-op rather than a race — it says "a meeting
 *     happened", not "go there".
 *
 * The `joinCode` check is a SECURITY BOUNDARY, not a defensive nicety: it is the
 * client half of the narrowing, and it is why the decliner's copy cannot be
 * turned back into an entry by a client that ignores `movedUserIds`.
 */
export function escalationFor(event: RealtimeEvent, meId: string): EscalationInvite | null {
  if (event.type !== 'call.escalated') return null;
  if (meId.length === 0 || event.actorId === meId) return null;
  if (!event.movedUserIds.some((userId) => userId === meId)) return null;
  const joinCode = event.joinCode ?? '';
  if (joinCode.length === 0 || event.meetingId.length === 0) return null;
  return { callId: event.callId, meetingId: event.meetingId, joinCode };
}

/**
 * Redeem the code. Resolves `true` when the meeting will open for us.
 *
 * NEVER THROWS. It is called from a socket callback, where an unhandled
 * rejection is invisible, and its failure is a thing the user has to be told
 * rather than a thing the console has to hold.
 */
export async function redeemEscalation(invite: EscalationInvite): Promise<boolean> {
  try {
    await apiPost<unknown>('/meetings/join-by-code', { joinCode: invite.joinCode });
    return true;
  } catch {
    return false;
  }
}

/**
 * IS THERE ANYBODY TO MOVE — the client half of the ring gate.
 *
 * "Move to meeting" was reachable for the whole 45-second ring, because the menu
 * was gated on the CALLER'S OWN MEDIA state and the caller publishes into the
 * empty room from the first second. Pressing it then moved a party of one: the
 * still-ringing callee was cancelled and marked missed, and their popup vanished
 * with no invite and no explanation — the exact experience the verb exists to
 * end, on the same button, in the window a user is most likely to press it.
 *
 * THE ENFORCEMENT IS THE SERVER'S (`escalationBlocker`, inside the transaction
 * that would otherwise end the call, because a client gate can be raced). This
 * is the courtesy half: do not OFFER a verb that cannot work.
 *
 * `party.user !== null` IS THE WHOLE OF THE BUG THIS REPLACES. The predicate
 * used to be `state === 'connected' || state === 'on_hold'` over `counterparts`,
 * and `counterparts` is `live.filter((p) => p.user?.id !== meId)` — a PSTN leg
 * has `user === null`, so `undefined !== meId` KEEPS the phone line. A connected
 * external caller therefore made this true, the item was offered, and the server
 * refused with 409 "An external caller cannot be moved to a meeting." That is
 * the correct refusal, from the one of the server's three rules that exists
 * precisely because a phone has no browser to navigate. The server drops the
 * same leg in `inConversationPartyIds`; this now draws the line in the same
 * place, so the two halves agree instead of contradicting each other.
 *
 * FALSE FOR AN EMPTY OR UNKNOWN ROSTER, deliberately: a client that cannot see
 * who is on the call must not offer a verb that ends it for everybody.
 */
const IN_CONVERSATION: ReadonlySet<CallPartyState> = new Set<CallPartyState>([
  'connected',
  'on_hold',
]);

export function canOfferEscalation(counterparts: readonly CallParticipant[]): boolean {
  return counterparts.some((party) => party.user !== null && IN_CONVERSATION.has(party.state));
}
